import { Skeleton } from '@mantine/core';
import { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Limit, SpendRate } from './components';

interface SpendRateUsageProps {
    isLoading?: boolean;
    rollingAverage?: number;
    currentSpendRate?: number;
    cloudQuotas?: number;
    cloudQuotaMax?: number;
    sshKeyQuotas?: number;
    sshKeyQuotaMax?: number;
}

const SpendRateSkeleton = memo(() => (
    <div className="grid w-full grid-cols-2 gap-6">
        <div className="flex flex-col items-start gap-1">
            <Skeleton height={18} width={120} />
            <Skeleton height={22} width={80} />
        </div>
        <div className="flex flex-col items-start gap-1">
            <Skeleton height={18} width={120} />
            <Skeleton height={22} width={80} />
        </div>
    </div>
));

const LimitsSkeleton = memo(() => (
    <div className="w-full flex flex-col justify-start items-start gap-4">
        {[0, 1].map((i) => (
            <div key={i} className="w-full flex flex-col justify-start items-start gap-1">
                <div className="w-full flex flex-row justify-between items-center">
                    <Skeleton height={18} width={i === 0 ? 120 : 100} />
                    <Skeleton height={18} width={60} />
                </div>
                <Skeleton height={18} width={'100%'} />
            </div>
        ))}
    </div>
));

function SpendRateUsage({
    isLoading = false,
    rollingAverage = 0,
    currentSpendRate = 0,
    cloudQuotas = 0,
    cloudQuotaMax,
    sshKeyQuotas = 0,
    sshKeyQuotaMax,
}: SpendRateUsageProps) {
    const { t } = useTranslation('dashboard');

    return (
        <div className="w-full flex flex-col justify-start items-start gap-1 overflow-hidden">
            <h2 className="text-xl">{t('spendRateUsage.title')}</h2>
            <p className="text-sm text-fg/68">{t('spendRateUsage.subtitle')}</p>
            <div className="mt-3.25 w-full flex flex-col justify-start items-start gap-4">
                <div className="w-full grid grid-cols-2 gap-6">
                    {isLoading ? (
                        <SpendRateSkeleton />
                    ) : (
                        <Fragment>
                            <SpendRate
                                label={t('spendRateUsage.rollingAverage')}
                                value={rollingAverage}
                                perLabel={t('spendRateUsage.perDay')}
                            />
                            <SpendRate
                                label={t('spendRateUsage.currentSpendRate')}
                                value={currentSpendRate}
                                perLabel={t('spendRateUsage.perHour')}
                            />
                        </Fragment>
                    )}
                </div>
                <div className="w-full flex flex-col justify-start items-start gap-4">
                    {isLoading ? (
                        <LimitsSkeleton />
                    ) : (
                        <Fragment>
                            <Limit
                                label={t('spendRateUsage.cloudComputeQuotas')}
                                value={cloudQuotas}
                                maxValue={cloudQuotaMax}
                            />
                            <Limit
                                label={t('spendRateUsage.sshKeyQuotas')}
                                value={sshKeyQuotas}
                                maxValue={sshKeyQuotaMax}
                            />
                        </Fragment>
                    )}
                </div>
            </div>
        </div>
    );
}

export default memo(SpendRateUsage);
